/**
 * Offline Sync Worker for PH Tablet POS
 * Handles background synchronization of offline sales
 */
import {
  getPendingOfflineSales,
  updateSaleSyncStatus,
  markSaleAsSynced,
  updateLastSyncTimestamp,
  SyncStatus
} from './offlineStore';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sync state
let isSyncing = false;
let syncListeners = [];
let retryTimeout = null;

// Exponential backoff config
const MIN_RETRY_DELAY = 1000;   // 1 second
const MAX_RETRY_DELAY = 60000;  // 1 minute
let currentRetryDelay = MIN_RETRY_DELAY;

/**
 * Subscribe to sync status updates
 */
export function subscribeSyncStatus(listener) {
  syncListeners.push(listener);
  return () => {
    syncListeners = syncListeners.filter(l => l !== listener);
  };
}

/**
 * Notify all listeners of status change
 */
function notifyListeners(status) {
  syncListeners.forEach(listener => listener(status));
}

/**
 * Sync a single offline sale to server
 */
async function syncSingleSale(sale, token) {
  const response = await fetch(`${API_URL}/api/pos/sales/sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      local_id: sale.local_id,
      idempotency_key: sale.idempotency_key,
      tenant_id: sale.tenant_id,
      branch_id: sale.branch_id,
      cashier_id: sale.cashier_id,
      created_at_local: sale.created_at_local,
      offline_receipt_number: sale.offline_receipt_number,
      items: sale.items,
      payment_method: sale.payment_method,
      payment_reference: sale.payment_reference,
      tendered_amount: sale.tendered_amount,
      discount_total: sale.discount_total,
      discount_type: sale.discount_type || 'regular',
      discount_customer_name: sale.discount_customer_name || null,
      discount_customer_id: sale.discount_customer_id || null,
      discount_remarks: sale.discount_remarks || null
    })
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Network error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  return response.json();
}

/**
 * Start sync process
 */
export async function startSync(token) {
  if (isSyncing || !navigator.onLine) return;
  
  isSyncing = true;
  notifyListeners({ type: 'SYNC_START' });
  
  try {
    const pendingSales = await getPendingOfflineSales();
    
    if (pendingSales.length === 0) {
      notifyListeners({ type: 'SYNC_COMPLETE', synced: 0, failed: 0 });
      isSyncing = false;
      return;
    }
    
    let synced = 0;
    let failed = 0;
    
    // Sync sequentially to maintain order
    for (const sale of pendingSales) {
      try {
        notifyListeners({ 
          type: 'SYNCING', 
          current: synced + failed + 1, 
          total: pendingSales.length 
        });
        
        await updateSaleSyncStatus(sale.local_id, SyncStatus.SYNCING);
        
        const result = await syncSingleSale(sale, token);
        
        if (result.success) {
          await markSaleAsSynced(
            sale.local_id,
            result.sale_id,
            result.receipt_number,
            sale.offline_receipt_number
          );
          synced++;
          
          // Reset retry delay on success
          currentRetryDelay = MIN_RETRY_DELAY;
        } else {
          await updateSaleSyncStatus(sale.local_id, SyncStatus.FAILED, result.message);
          failed++;
        }
        
      } catch (error) {
        console.error(`Failed to sync sale ${sale.local_id}:`, error);
        await updateSaleSyncStatus(sale.local_id, SyncStatus.FAILED, error.message);
        failed++;
      }
    }
    
    await updateLastSyncTimestamp();
    
    notifyListeners({ type: 'SYNC_COMPLETE', synced, failed });
    
    // Schedule retry if there were failures
    if (failed > 0) {
      scheduleRetry(token);
    }
    
  } catch (error) {
    console.error('Sync process error:', error);
    notifyListeners({ type: 'SYNC_ERROR', error: error.message });
    scheduleRetry(token);
  } finally {
    isSyncing = false;
  }
}

/**
 * Schedule a retry with exponential backoff
 */
function scheduleRetry(token) {
  if (retryTimeout) clearTimeout(retryTimeout);
  
  retryTimeout = setTimeout(() => {
    if (navigator.onLine) {
      startSync(token);
    }
  }, currentRetryDelay);
  
  // Increase delay for next retry (exponential backoff)
  currentRetryDelay = Math.min(currentRetryDelay * 2, MAX_RETRY_DELAY);
  
  notifyListeners({ 
    type: 'RETRY_SCHEDULED', 
    delay: currentRetryDelay / 1000 
  });
}

/**
 * Cancel scheduled retry
 */
export function cancelRetry() {
  if (retryTimeout) {
    clearTimeout(retryTimeout);
    retryTimeout = null;
  }
}

/**
 * Force immediate sync
 */
export async function forceSync(token) {
  cancelRetry();
  currentRetryDelay = MIN_RETRY_DELAY;
  await startSync(token);
}

/**
 * Get current sync status
 */
export function getSyncingStatus() {
  return { isSyncing };
}
